
import { useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import PropTypes from "prop-types";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";
import Breadcrumbs from "examples/Breadcrumbs";
import NotificationItem from "examples/Items/NotificationItem";
import { Modal, Typography, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import { useNavigate } from "react-router-dom"; 
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "examples/Navbars/DashboardNavbar/styles";
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";

function DashboardNavbar({ absolute, light, isMini }) {
  const [navbarType, setNavbarType] = useState();
  const [error, setError] = useState();
  const [controller, dispatch] = useMaterialUIController();
  const [userInfo, setUserInfo] = useState(null); // Initialize user info state
  const {
    miniSidenav,
    transparentNavbar,
    fixedNavbar,
    openConfigurator,
    darkMode,
  } = controller;
  const [openMenu, setOpenMenu] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [openLogout, setOpenLogout] = useState(false);
  const [openProfileModal, setOpenProfileModal] = useState(false);
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const [openLogoutConfirmModal, setOpenLogoutConfirmModal] = useState(false);
  const route = useLocation().pathname.split("/").slice(1);
  const navigate = useNavigate();

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(
        dispatch,
        (fixedNavbar && window.scrollY === 0) || !fixedNavbar
      );
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const fetchProfileData = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      if (!token) {
        console.error("No token found in localStorage");
        setError("User not authenticated");
        return;
      }
  
      const response = await axios.get("http://localhost:9000/api/user/myprofile", {
        headers: {
          Authorization: `Bearer ${token}`, // Attach token in the request
        },
      });
  
      console.log("Profile data:", response.data);
      setUserInfo(response.data.user);
    } catch (error) {
      console.error("Error fetching profile data:", error.response?.data || error.message);
      setError("Unauthorized: Please log in again.");
    }
  };

  useEffect(() =>{
    fetchProfileData();
  },[]);

  const handleLogout = () => {
    // Handle logout logic (e.g., clearing tokens, redirecting, etc.)
    localStorage.removeItem("accessToken"); // Clear the token
    navigate("http://localhost:9000/api/user/login");
    window.location.reload(); // Optionally reload the page to reset the state
    
  };
  
  const handleCloseLogoutModal = () => {
    setOpenLogoutModal(false); // Close the modal when the cancel button is clicked
  };

  const handleOpenLogoutConfirmModal = () => {
    setOpenLogoutModal(true); // Set state to true to show the modal
  };  
  

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);
  const handleOpenMenu = (event) => setOpenMenu(event.currentTarget);
  const handleOpenProfile = (event) => setOpenProfile(event.currentTarget);

  const handleCloseMenu = () => setOpenMenu(false);
  const handleCloseProfile = () => setOpenProfile(false);

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>email</Icon>} title="Check new messages" />
      <NotificationItem
        icon={<Icon>podcasts</Icon>}
        title="Manage Podcast sessions"
      />
      <NotificationItem
        icon={<Icon>shopping_cart</Icon>}
        title="Payment successfully completed"
      />
    </Menu>
  );
  
  const profileMenu = () => (
    <>  
      {/* Dropdown Menu */}
      <Menu
        anchorEl={openProfile}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={Boolean(openProfile)}
        onClose={() => setTimeout(() => setOpenProfile(false), 200)} // Prevents auto-close issue
        sx={{ mt: 2 }}
      >
        {userInfo && (
          <MDBox px={2} py={1} display="flex" alignItems="center">
            <Icon sx={{ mr: 1, fontSize: "24px", color: "primary.main" }}>account_circle</Icon>
            <MDBox>
              <MDBox fontWeight="bold">{userInfo.name}</MDBox>
              <MDBox fontSize="small" color="text.secondary">{userInfo.email}</MDBox>
            </MDBox>
          </MDBox>
        )}
        
        {/* Click "Profile" to Open Profile Modal */}
        <NotificationItem
          icon={<Icon>shopping_cart</Icon>}
          title="Profile"
          onClick={() => {
            setOpenProfileModal(true); // Open Profile Modal
            setOpenProfile(false); // Close dropdown
          }}
        />
        <NotificationItem icon={<Icon>podcasts</Icon>} title="Log Out" 
          onClick={handleOpenLogoutConfirmModal}
      />
      </Menu>
  
      {/* Profile Modal */}
      <Modal open={openProfileModal} onClose={() => setOpenProfileModal(false)}>
        <MDBox
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            minWidth: 400,
          }}
        >
  
          {userInfo ? (
            <>
              <Typography variant="body1"><strong>Name:</strong> {userInfo.name}</Typography>
              <Typography variant="body1"><strong>Email:</strong> {userInfo.email}</Typography>
              <Typography variant="body1"><strong>Mobile:</strong> {userInfo.mobileno}</Typography>
              <Typography variant="body1"><strong>Role:</strong> {userInfo.role}</Typography>
              <Typography variant="body1">
                <strong>Created At:</strong> {new Date(userInfo.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="body1">
                <strong>Status:</strong> {userInfo.isActive ? "Active" : "Inactive"}
              </Typography>
            </>
          ) : (
            <Typography color="error">No user data available.</Typography>
          )}
  
          <MDBox mt={2} display="flex" justifyContent="flex-end">
            <Button variant="contained" color="primary" onClick={() => setOpenProfileModal(false)}>
              Close
            </Button>
          </MDBox>
        </MDBox>
      </Modal>

      {/* Logout Modal */}
      <Modal open={openLogoutModal} onClose={handleCloseLogoutModal}>
        <MDBox
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            minWidth: 400,
          }}
        >
          <Typography variant="h6">{userInfo?.name} Are you sure you want to logout?</Typography>

          <MDBox mt={2} display="flex" justifyContent="space-between">
            <Button
              variant="contained"
              color="primary"
              onClick={handleLogout} // OK Button - Handle Logout
            >
              OK
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleCloseLogoutModal} // Cancel Button - Close Modal
            >
              Cancel
            </Button>
          </MDBox>
        </MDBox>
      </Modal>

    </>
  );
  

  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba },
  }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) =>
        navbar(theme, { transparentNavbar, absolute, light, darkMode })
      }
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" sx={(theme) => navbarRow(theme, { isMini })}>
          {/* <IconButton
            sx={navbarDesktopMenu}
            onClick={handleMiniSidenav}
            size="small"
            disableRipple
          >
            <Icon fontSize="medium" sx={iconsStyle}>
              {miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </IconButton> */}
          <Breadcrumbs
            icon="home"
            title={route[route.length - 1]}
            route={route}
            light={light}
          />
          <MDBox pr={1} ml={5}>
            <MDButton 
              variant="contained"
              component={Link} 
              color="black"
              to="/dashboards/analytics"
            >
              Analytics
            </MDButton>
          </MDBox>
          {/* <MDBox display="flex" alignItems="center" pr={1} ml={1} width="100%">
            <MDInput 
              placeholder="Type your message..." 
              multiline 
              rows={1} 
              fullWidth 
              sx={{ flex: 1, mr: 2 }} // Makes input expand and adds margin
            />

            <MDButton 
              variant="contained" 
              color="primary"
              sx={{ minWidth: "50px", height: "40px" }} // Adjusts button size
            >
              Send
            </MDButton>
          </MDBox> */}

        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox pr={1}>
              <MDInput label="Search here" />
            </MDBox>
            <MDBox color={light ? "white" : "inherit"}>
              {/* <Link to="/authentication/sign-in/basic"> */}
                <IconButton sx={navbarIconButton} size="small" disableRipple 
                 color="inherit"
                //  aria-controls="notification-menu"
                 aria-haspopup="true"
                 variant="contained"
                 onClick={handleOpenProfile}>
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
              {/* </Link> */}
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarMobileMenu}
                onClick={handleMiniSidenav}
              >
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu_open" : "menu"}
                </Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                onClick={handleConfiguratorOpen}
              >
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                variant="contained"
                onClick={handleOpenMenu}
              >
                <MDBadge badgeContent={9} color="error" size="xs" circular>
                  <Icon sx={iconsStyle}>notifications</Icon>
                </MDBadge>
              </IconButton>
              {renderMenu()}
              {profileMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
